import type { SurveyBuilderManager } from '@/state/surveys/useSurveyBuilder';
import type { TargetingRule, TrackedDevice } from '@/state/surveys/survey-targeting';
import { cn } from 'helpers/cn';
import { TRACKED_DEVICE_TYPES } from 'definitions';
import { Input } from '@/components/ui/input';
import { MAX_RULES, devicesOf, urlRulesOf, withRules } from '@/state/surveys/survey-targeting';
import { useSurveyExperimentOptions } from '@/state/surveys/useSurveyExperimentOptions';
import { ExperimentPicker } from './ExperimentPicker';
import { PageRules } from './PageRules';

type StepTargetingProps = { manager: SurveyBuilderManager };

const FIELD_INPUT = 'border-line bg-field text-foreground h-9 rounded-lg text-body';

const PILL = 'micro-label rounded-lg border px-3 py-1.5 transition-colors duration-150';
const PILL_ON = 'bg-sel-bg border-sel-line text-foreground';
const PILL_OFF = 'border-line bg-panel2 text-dim hover:bg-hover hover:text-foreground';

const LINKED = 'Only visitors in this experiment see the survey, and its results split by variant.';

// The survey shows while the experiment assigns visitors, which only a running or
// deployed one does, so the others say plainly that no one will see it. A QA preview
// assigns nobody, so a survey tied to an experiment in QA does not show there either.
const LINK_NOTE: Record<string, string> = {
  Draft: `${LINKED} This experiment is a draft, so no one sees the survey until it starts.`,
  QA: `${LINKED} This experiment is in QA, so no one sees the survey until it starts.`,
  Ended: `${LINKED} This experiment has ended, so no one sees the survey.`,
};

export const StepTargeting = ({ manager }: StepTargetingProps) => {
  const { draft, updateDraft, targetingProblem, step, websiteId } = manager;
  // Fetched once the step is open, or straight away when there is a link to name.
  const { experiments, isLoading } = useSurveyExperimentOptions({
    websiteId,
    enabled: step === 'Targeting' || draft.experimentId !== null,
  });
  const urlRules = urlRulesOf(draft);
  const devices = devicesOf(draft);
  const linked = experiments.find((experiment) => experiment.id === draft.experimentId);

  const setRules = (nextUrlRules: TargetingRule[], nextDevices: readonly TrackedDevice[]) =>
    updateDraft((state) => ({ ...state, targeting: withRules({ urlRules: nextUrlRules, devices: nextDevices }) }));

  // None selected and all selected both mean every device; toggling the last one
  // off returns to "every device" rather than to "no device".
  const toggleDevice = (device: TrackedDevice) => () =>
    setRules(urlRules, devices.includes(device) ? devices.filter((d) => d !== device) : [...devices, device]);

  const setExperiment = (experimentId: number | null) =>
    updateDraft((state) => ({ ...state, experimentId, variantIds: [] }));

  // Ids of variants the experiment no longer has are dropped on any toggle: they
  // have no pill to clear them with, and the API refuses a link that names one.
  const toggleVariant = (variantId: number) => () =>
    updateDraft((state) => {
      const live = state.variantIds.filter((id) => linked?.variants.some((variant) => variant.id === id));
      return {
        ...state,
        variantIds: live.includes(variantId) ? live.filter((id) => id !== variantId) : [...live, variantId],
      };
    });

  const setSampleRate = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const sampleRate = Number(ev.target.value);
    updateDraft((state) => ({ ...state, sampleRate }));
  };

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h2 className="text-foreground m-0 text-lede font-medium">Who sees it</h2>
        <p className="supporting m-0">
          Every rule narrows the audience. With none, the survey can show to every visitor on every page.
        </p>
      </div>

      <section className="space-y-2">
        <span className="micro-label block">Pages</span>
        <PageRules
          rules={urlRules}
          canAdd={draft.targeting.length < MAX_RULES}
          onChange={(nextUrlRules) => setRules(nextUrlRules, devices)}
        />
      </section>

      <section className="space-y-2">
        <span className="micro-label block">Devices</span>
        <div className="flex flex-wrap gap-2">
          {TRACKED_DEVICE_TYPES.map((device) => (
            <button
              key={device}
              type="button"
              aria-pressed={devices.includes(device)}
              onClick={toggleDevice(device)}
              className={cn(PILL, devices.includes(device) ? PILL_ON : PILL_OFF)}
            >
              {device}
            </button>
          ))}
        </div>
        {devices.length === 0 && <p className="supporting m-0">Every device.</p>}
      </section>

      <section className="space-y-2">
        <label htmlFor="survey-experiment" className="micro-label block">
          Experiment
        </label>
        <ExperimentPicker
          id="survey-experiment"
          experiments={experiments}
          experimentId={draft.experimentId}
          isLoading={isLoading}
          onSelect={setExperiment}
        />
        {draft.experimentId !== null && (
          <>
            <p className="supporting m-0">{(linked && LINK_NOTE[linked.status]) ?? LINKED}</p>
            {linked && linked.variants.length > 0 && (
              <div className="space-y-1.5">
                <span className="micro-label block">Variants</span>
                <div className="flex flex-wrap gap-2">
                  {linked.variants.map((variant) => (
                    <button
                      key={variant.id}
                      type="button"
                      aria-pressed={draft.variantIds.includes(variant.id)}
                      onClick={toggleVariant(variant.id)}
                      className={cn(PILL, draft.variantIds.includes(variant.id) ? PILL_ON : PILL_OFF)}
                    >
                      {variant.name}
                    </button>
                  ))}
                </div>
                {draft.variantIds.length === 0 && <p className="supporting m-0">Every variant.</p>}
              </div>
            )}
          </>
        )}
      </section>

      <section className="space-y-1.5">
        <label htmlFor="survey-sample" className="micro-label block">
          Share of these visitors to ask (%)
        </label>
        <Input
          id="survey-sample"
          type="number"
          min={1}
          max={100}
          step={1}
          value={draft.sampleRate}
          onChange={setSampleRate}
          className={cn(FIELD_INPUT, 'w-28')}
        />
        <p className="supporting m-0">
          Each visitor is in or out once, and stays that way. Lower it to ask fewer people on a busy page.
        </p>
      </section>

      {targetingProblem && <p className="text-danger m-0 text-micro">{targetingProblem}</p>}
    </div>
  );
};
